import React, { useEffect, useRef, useState } from 'react';
import './project.css';
import MobileNav from '../common/MobileNav';

const Projects = () => {
  const heroRef = useRef(null);

  const [scrollProgress, setScrollProgress] = useState(0);      
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
  let ticking = false;
  
  const handleScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        // Parallax effect
        if (heroRef.current) {
          const scrolled = window.pageYOffset;
          heroRef.current.style.transform = `translateY(${scrolled * 0.4}px)`;
        }

        // Scroll progress
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollTop = window.pageYOffset;
        const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
        setScrollProgress(progress);

        // Back to top button
        setShowBackToTop(scrollTop > 300);

        ticking = false;
      });
      ticking = true;
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  return () => window.removeEventListener('scroll', handleScroll);
}, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.2 }
    );

    const animatedElements = document.querySelectorAll('.projects-stat-card, .projects-project-card, .projects-product-card');
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const stats = [
    {
      value: '3',
      label: 'Success in getting happy customer',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      )
    },
    {
      value: '5',
      label: 'Successful business',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="20" x2="12" y2="10" />
          <line x1="18" y1="20" x2="18" y2="4" />
          <line x1="6" y1="20" x2="6" y2="16" />
        </svg>
      )
    },
    {
      value: '6',
      label: 'Total clients who love Elexyt',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      )
    },
    {
      value: '4',
      label: 'Stars reviews given by satisfied clients',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      )
    }
  ];

  const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

  const projects = [
    {
      title: 'Web Design',
      category: 'Web Analysis',
      description: 'Modern, responsive web design solutions that captivate users and drive engagement.',
      image: 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=800',
      tags: ['UI/UX', 'Responsive', 'Modern']
    },
    {
      title: 'Cyber Security',
      category: 'Cyber Security Core',
      description: 'Enterprise-grade security solutions to protect your digital assets and infrastructure.',
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800',
      tags: ['Security', 'Protection', 'Monitoring']
    },
    {
      title: 'Mobile Application',
      category: 'Mobile Apps',
      description: 'Native and cross-platform mobile apps that deliver exceptional user experiences.',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800',
      tags: ['iOS', 'Android', 'React Native']
    },
    {
      title: 'Web Development',
      category: 'Web Analysis',
      description: 'Full-stack web development with cutting-edge technologies and best practices.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800',
      tags: ['Frontend', 'Backend', 'API']
    },
    {
      title: 'Digital Marketing',
      category: 'Marketing Analysis',
      description: 'Data-driven marketing strategies that boost your online presence and ROI.',
      image: 'https://images.unsplash.com/photo-1562577309-4932fdd64cd1?w=800',
      tags: ['SEO', 'Analytics', 'Strategy']
    }
  ];

  const products = [
    {
      title: 'RFP (Request For Proposal)',
      description: 'Request for proposal (RFP) software, sometimes referred to as request for quotation (RFQ) software, helps organization create and manage RFP documents, as well as the processes used for vendors to respond to proposals or questionnaires outlining their products or services, pricing, and other relevant information.',
      note: 'It can be customized as per requirement.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800',
      features: ['Document Management', 'Vendor Response', 'Customizable Workflows']
    },
    {
      title: 'HRMS (Human Resource Management Systems)',
      description: 'The Human Resource Management System (HRMS) is a comprehensive solution that encompasses a wide range of critical HR functions, designed to meet the diverse needs of organizations. At its core, the HRMS facilitates New Hire management, Leave management, Project management, Timesheet management, Payroll management.',
      note: 'It can be customized as per requirement.',
      image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800',
      features: ['Employee Management', 'Payroll System', 'Leave Tracking']
    },
    {
      title: 'e-Learning Portal',
      description: 'Its primary purpose is to provide users with a centralized platform where they can access a wealth of study materials, including video lectures, practice tests, and interactive modules. The portal aims to simplify the learning journey, making it easier for students to find the resources they need to succeed.',
      note: 'It can be customized as per requirement.',
      image: 'https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=800',
      features: ['Video Lectures', 'Interactive Modules', 'Progress Tracking']
    },
    {
      title: 'Garment Management System',
      description: 'Garment Management System is designed for Pharma industry where it can track the garments used in lab and keep safe and maintainable digitally.',
      note: 'It can be customized as per requirement.',
      image: 'https://images.unsplash.com/photo-1576086213369-97a306d36557?w=800',
      features: ['Digital Tracking', 'Inventory Management', 'Compliance Ready']
    }
  ];

  return (
    <>
    {/* Scroll Progress Bar */}
    <div className="scroll-progress" style={{ width: `${scrollProgress}%` }} />
    <div className="projects-page">
      <section className="projects-hero">
        <div className="projects-hero-background" ref={heroRef}>
          <div className="projects-hero-pattern"></div>
          <div className="projects-hero-gradient"></div>
        </div>

        <div className="container">
          <div className="projects-hero-content">
            <div className="projects-hero-badge">
              <span className="badge-dot"></span>
              Our Portfolio
            </div>
            <h1 className="projects-hero-title">
              Transforming Ideas Into
              <span className="projects-hero-title-gradient"> Reality</span>
            </h1>
            <p className="projects-hero-description">
              Explore our diverse range of successful projects and innovative products that have helped businesses achieve their digital transformation goals.
            </p>
          </div>
        </div>

        {/* <div className="projects-hero-stats">
          <div className="container">
            <div className="projects-stats-wrapper">
              {stats.map((stat, index) => (
                <div key={index} className="projects-stat-card">
                  <div className="projects-stat-icon">{stat.icon}</div>
                  <div className="projects-stat-value">{stat.value}</div>
                  <div className="projects-stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div> */}
      </section>

      <section className="projects-showcase-section">
        <div className="container">
          <div className="section-header centered">
            <div className="section-label">
              <span className="label-line"></span>
              <span>Our Projects</span>
              <span className="label-line"></span>
            </div>
            <h2 className="section-title-large">
              Recently Completed Projects
            </h2>
            <p className="section-subtitle">
              Delivering excellence through innovative solutions and cutting-edge technology
            </p>
          </div>

          <div className="projects-showcase-grid">
            {projects.map((project, index) => (
              <div key={index} className="projects-project-card">
                <div className="projects-project-image-wrapper">
                  <img src={project.image} alt={project.title} />
                  <div className="projects-project-overlay">
                    <div className="projects-project-tags">
                      {project.tags.map((tag, tagIndex) => (
                        <span key={tagIndex} className="projects-project-tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="projects-project-content">
                  <span className="projects-project-category">{project.category}</span>
                  <h3 className="projects-project-title">{project.title}</h3>
                  <p className="projects-project-description">{project.description}</p>
                  
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="projects-products-section">
        <div className="container">
          <div className="section-header centered">
            <div className="section-label">
              <span className="label-line"></span>
              <span>Our Products</span>
              <span className="label-line"></span>
            </div>
            <h2 className="section-title-large">
              Enterprise Solutions That Scale
            </h2>
            <p className="section-subtitle">
              Powerful, customizable products designed to streamline your business operations
            </p>
          </div>

          <div className="projects-products-grid">
            {products.map((product, index) => (
              <div key={index} className="projects-product-card">
                <div className="projects-product-image-wrapper">
                  <img src={product.image} alt={product.title} />
                  <div className="projects-product-image-overlay"></div>
                </div>
                <div className="projects-product-content">
                  <h3 className="projects-product-title">{product.title}</h3>
                  <p className="projects-product-description">{product.description}</p>
                  
                  <div className="projects-product-features">
                    {product.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="projects-product-feature">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="projects-product-footer">
                    <p className="projects-product-note">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                      {product.note}
                    </p>
                    {/* <a href="#" className="projects-product-link">
                      Learn More
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="7" y1="17" x2="17" y2="7" />
                        <polyline points="7 7 17 7 17 17" />
                      </svg>
                    </a> */}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="projects-cta-section">
        <div className="cta-pattern"></div>
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">
              Have a Project in Mind?
            </h2>
            <p className="cta-description">
              Let's collaborate to bring your vision to life. Our team is ready to deliver exceptional results tailored to your unique needs.
            </p>
            <div className="cta-actions">
              <a href="/contact" className="btn btn-light btn-large">
                Start Your Project
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
              <a href="/service" className="btn btn-outline-light-alt btn-large">
                View Services
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>

    <button
      className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
      onClick={scrollToTop}
      aria-label="Scroll to top"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="18 15 12 9 6 15" />
      </svg>
    </button>
<MobileNav />
    </>
  );
};

export default Projects;